import { SubdirectoryArrowRightOutlined } from "@mui/icons-material";
import { Button } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { CustomerReportModel } from "../../../../../Generated/Commodity-Management-Api";
import { GroupingOption } from "../../../../../Types";
import { StoneXDivider, StoneXRow } from "../../../../StoneX";
import { StoneXAutocomplete, StoneXModal } from "../../../../StoneXMui";
import { StoneXTransferList } from "../../../../StoneXMui/StoneXTransferList/StoneXTransferList";
import { PositionColumns, PositionReportColumn, PositionReportConfiguration, PositionReportUserOverride } from "./Types";
import { difference, intersection } from "lodash";
import { useApplicationDispatch } from "../../../../../Redux/ReduxStore";
import { setCustomerReportUserOverride } from "../../../../../Redux/Slices/CommodityManagementSlice";
import { CommodityManagementApi } from "../../../../../Apis/Apis";

interface PositionReportSettingsModal {
    customerReport: CustomerReportModel;
    open: boolean;
    groupingOptions: GroupingOption[];
    onClose: () => void;
    onSave: (customerReport: CustomerReportModel) => void;
}

export default function PositionReportSettingsModal(props: PositionReportSettingsModal) {

    const { customerReport, open, groupingOptions, onClose, onSave } = props;
    const dispatch = useApplicationDispatch();

    const reportConfiguration = useMemo(() => JSON.parse(customerReport.configuration!) as PositionReportConfiguration, [customerReport]);
    const [ userOverrides, setUserOverrides ] = useState<PositionReportUserOverride | undefined>();
    useEffect(updateUserOverrides, [customerReport]);
    
    const [ hiddenColumns, setHiddenColumns ] = useState<PositionReportColumn[]>([]);
    const [ activeColumns, setActiveColumns ] = useState<PositionReportColumn[]>([]);
    useEffect(updateColumns, [reportConfiguration, userOverrides]);

    const [ updatedActiveColumns, setUpdatedActiveColumns ] = useState<PositionReportColumn[]>();
    // const [ updatedHiddenColumns, setUpdatedHiddenColumns ] = useState<PositionReportColumn[]>();

    const positionColumnOrderMap = useMemo<{[key: string]: number}>(() => {
      const keys = Object.keys(PositionColumns);
      const map: {[key: string]: number} = {};
      keys.forEach((value: string, index) => map[value] = index);
      return map; 
    }, []);

    const [ level1GroupingOption, setLevel1GroupingOption ] = useState<GroupingOption | null>(getLevel1GroupingFromConfiguration());
    const [ level2GroupingOption, setLevel2GroupingOption ] = useState<GroupingOption | null>(getLevel2GroupingFromConfiguration());

    useEffect(() => {
      setLevel1GroupingOption(getLevel1GroupingFromConfiguration());
      setLevel2GroupingOption(getLevel2GroupingFromConfiguration());
    }, [userOverrides, reportConfiguration]);

    function getLevel1GroupingFromConfiguration() {
      return userOverrides?.behavior?.grouping ? userOverrides.behavior.grouping?.[0] : reportConfiguration?.behavior?.grouping?.[0] ?? null;
    }

    function getLevel2GroupingFromConfiguration() {
      return userOverrides?.behavior?.grouping ? userOverrides.behavior.grouping?.[1] : reportConfiguration?.behavior?.grouping?.[1] ?? null;
    }

    function save() {
      const activeColumnsToSave = updatedActiveColumns ?? activeColumns;
      // const hiddenColumnsToSave = updatedHiddenColumns ?? hiddenColumns;

      // Alwasys overwrite grouping option as there currently is no way to detect if user meant to override those settings.
      const groupingOptionsToSave = [ level1GroupingOption, level2GroupingOption ].filter(x => x !== null) ?? undefined;

      const userSettings = {
        behavior: {
          grouping: groupingOptionsToSave
        },
        columns: {
          active: activeColumnsToSave,
          // hidden: hiddenColumnsToSave
        }
      } as PositionReportUserOverride;

      const userOverrides = JSON.stringify(userSettings);

      CommodityManagementApi.updateCustomerReportUserOverrides(undefined, customerReport.customerReportId, userOverrides);

      dispatch(
        setCustomerReportUserOverride({
          customerReportId: customerReport.customerReportId, 
          userOverrides: userOverrides
        })
      )

      onSave({...customerReport, userOverrides: userOverrides});
    }
    
    function updateUserOverrides() {
      setUserOverrides(customerReport.userOverrides ? JSON.parse(customerReport.userOverrides) as PositionReportUserOverride : undefined)
    }

    function updateColumns() {
      const availableColumns = reportConfiguration.columns.available ?? [];
      const activeColumns = userOverrides?.columns?.active ?? reportConfiguration.columns.active ?? [];
      const hiddenColumns = difference(availableColumns, activeColumns);
      setHiddenColumns(hiddenColumns);
      setActiveColumns(activeColumns);
    }

    function reset() {
      // Reset to customer settings. Empty the overrides
      setUserOverrides(undefined);
      updateColumns();
      setLevel1GroupingOption(reportConfiguration?.behavior?.grouping?.[0] ?? null);
      setLevel2GroupingOption(reportConfiguration?.behavior?.grouping?.[1] ?? null);
    }

    function onTransferListUpdate(hiddenItems: PositionReportColumn[], activeItems: PositionReportColumn[]) {
      // setUpdatedHiddenColumns(hiddenItems);
      setUpdatedActiveColumns(activeItems);
    }


    return (
        <StoneXModal header={customerReport.reportName + ' Report Configuration'} width='850px' open={open} onClose={onClose}>
          
          <h2>Rows</h2>
          <p></p>
          <StoneXRow>
            <StoneXAutocomplete 
              label="Group By (Optional)"
              options={groupingOptions} 
              value={level1GroupingOption}
              onUpdate={setLevel1GroupingOption}
              getOptionLabel={x => x} 
            />
          </StoneXRow>
          
          <StoneXRow align="start">
            <SubdirectoryArrowRightOutlined />
            <StoneXAutocomplete 
              label="Then By (Optional)"
              options={groupingOptions} 
              value={level2GroupingOption}
              onUpdate={setLevel2GroupingOption}
              getOptionLabel={x => x} 
            />
          </StoneXRow>
          

          <StoneXDivider />

          <h2>Columns</h2>

          <p>Click on one or multiple items to select, then use the arrows to move. </p>

          <StoneXTransferList 
            leftList={hiddenColumns} 
            leftHeader="Available Columns"
            rightList={activeColumns}
            rightHeader="Active Columns"
            getId={x => PositionColumns[x].colId}
            getGroup={x => PositionColumns[x].parentHeader}
            getSortingOrder={x => positionColumnOrderMap[x]}
            getOptionLabel={x => PositionColumns[x].columnName ?? PositionColumns[x].headerName ?? x}
            onUpdate={onTransferListUpdate}
          />

          <StoneXRow justify="end" marginDirection='top'>
            <Button variant="outlined" onClick={onClose}> Cancel </Button>
            <Button variant="outlined" onClick={reset}> Reset </Button>
            <Button onClick={save}> Set </Button>
          </StoneXRow>
        </StoneXModal>
    );
}
    
